import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Grid } from "@mui/material";
import { MASK_TYPE, TextField, InputAdornment } from "@curves/ui-base";
import { ProtectedInputField } from "@icoach/components";
import { getErrorModuleStatus, isFunction, refIsRequiredError, isEqual } from "@util/utils";
import useCreditCardState from "@icoach/components/creditCard/useCreditCardState";
import { CREDIT_CARD_TYPE } from "@icoach/constants/";
import { CARDFIELDMAP, CREDIT_CARD_TYPE_ICON } from "@icoach/components/creditCard/creditCard.config";
import { detectCardType, getInitialCreditCardInfo } from "@icoach/components/creditCard/creditCard.util";
import { isEmpty } from "lodash";

const CreditCardFields = forwardRef(
	(
		{
			defaultValue = {},
			errorModule,
			required = false,
			readOnly = false,
			showFields = [CARDFIELDMAP.CARDNO, CARDFIELDMAP.EXPIRYDATE, CARDFIELDMAP.BANKNAME, CARDFIELDMAP.OWNER],
			gridSizeMap = {},
			onFieldChange,
		},
		ref,
	) => {
		const fieldRefs = useRef({});
		const [creditCardType, setCreditCardType] = useState(null);
		const initialCreditCardInfo = useMemo(
			() => getInitialCreditCardInfo(defaultValue, showFields),
			// eslint-disable-next-line
			[defaultValue, showFields],
		);

		const { creditCardInfo, onUpdateField, onCreditCard, replaceCreditCardInfo } = useCreditCardState(initialCreditCardInfo);

		// 額外處理信用卡輸入時直接給卡片類型
		const handleCreditCardType = (keyName, num) => {
			if (!isEqual(keyName, CARDFIELDMAP.CARDNO)) return;
			const type = detectCardType(num);
			if ([CREDIT_CARD_TYPE.JCB, CREDIT_CARD_TYPE.MasterCard, CREDIT_CARD_TYPE.VISA, CREDIT_CARD_TYPE.OTHER].some(v => isEqual(v, type))) {
				onUpdateField(CARDFIELDMAP.CARDTYPE, type);
			}
			setCreditCardType(type);
		};

		const defaultIcon = useCallback(
			(defaultValue, defaultType) => {
				if (isEmpty(defaultValue)) {
					return React.Fragment;
				} else if (String(defaultValue).includes("*")) {
					const type = detectCardType(defaultValue);
					return CREDIT_CARD_TYPE_ICON[type];
				} else {
					return creditCardType ? CREDIT_CARD_TYPE_ICON[creditCardType] || React.Fragment : CREDIT_CARD_TYPE_ICON[defaultType];
				}
			},
			[creditCardType],
		);

		const handleOnChange = fieldName => e => {
			const value = e.target.value;
			handleCreditCardType(fieldName, value);
			onUpdateField(fieldName, value);
			isFunction(onFieldChange) && onFieldChange(fieldName, value);
		};

		const renderFields = field => {
			const Icon = defaultIcon(creditCardInfo[CARDFIELDMAP.CARDNO], creditCardInfo[CARDFIELDMAP.CARDTYPE]);
			switch (field) {
				case CARDFIELDMAP.CARDNO:
					return (
						<Grid key={field} xs={gridSizeMap[CARDFIELDMAP.CARDNO] || 6} item>
							<ProtectedInputField
								label="信用卡卡號"
								ref={ref => (fieldRefs.current[CARDFIELDMAP.CARDNO] = ref)}
								value={creditCardInfo.creditCardNo || ""}
								{...getErrorModuleStatus(errorModule, CARDFIELDMAP.CARDNO)}
								onChange={handleOnChange(CARDFIELDMAP.CARDNO)}
								InputProps={{
									endAdornment: (
										<InputAdornment position={"end"}>
											<Icon />
										</InputAdornment>
									),
								}}
								as={TextField}
								maskType={MASK_TYPE.CCN}
								readOnly={readOnly}
								required={required}
								fullWidth
							/>
						</Grid>
					);
				case CARDFIELDMAP.EXPIRYDATE:
					return (
						<Grid key={field} xs={gridSizeMap[CARDFIELDMAP.EXPIRYDATE] || 6} item>
							<TextField
								label="到期日 月/年份"
								ref={ref => (fieldRefs.current[CARDFIELDMAP.EXPIRYDATE] = ref)}
								value={creditCardInfo.cardExpiryDate || ""}
								{...getErrorModuleStatus(errorModule, CARDFIELDMAP.EXPIRYDATE)}
								onChange={handleOnChange(CARDFIELDMAP.EXPIRYDATE)}
								maskType={MASK_TYPE.CVV2}
								readOnly={readOnly}
								required={required}
								fullWidth
							/>
						</Grid>
					);
				case CARDFIELDMAP.BANKNAME:
					return (
						<Grid key={field} xs={gridSizeMap[CARDFIELDMAP.BANKNAME] || 6} item>
							<TextField
								label="發卡銀行"
								ref={ref => (fieldRefs.current[CARDFIELDMAP.BANKNAME] = ref)}
								value={creditCardInfo?.creditCardBankName || ""}
								{...getErrorModuleStatus(errorModule, CARDFIELDMAP.BANKNAME)}
								onChange={handleOnChange(CARDFIELDMAP.BANKNAME)}
								readOnly={readOnly}
								fullWidth
							/>
						</Grid>
					);
				case CARDFIELDMAP.OWNER:
					return (
						<Grid key={field} xs={gridSizeMap[CARDFIELDMAP.OWNER] || 6} item>
							<TextField
								label="持卡人姓名"
								ref={ref => (fieldRefs.current[CARDFIELDMAP.OWNER] = ref)}
								value={creditCardInfo?.cardName || ""}
								{...getErrorModuleStatus(errorModule, CARDFIELDMAP.OWNER)}
								onChange={handleOnChange(CARDFIELDMAP.OWNER)}
								readOnly={readOnly}
								fullWidth
							/>
						</Grid>
					);
				default:
					return null;
			}
		};

		// 解決一開始初始化有值不修改卡號自動判斷卡片類型帶入
		useEffect(
			() => {
				if (!creditCardType && !isEqual(creditCardType, creditCardInfo.creditCardType)) {
					const type = detectCardType(creditCardInfo.creditCardNo);
					onCreditCard(type);
				}
			},
			// eslint-disable-next-line
			[creditCardInfo.creditCardType, creditCardType],
		);

		useImperativeHandle(
			ref,
			() => ({
				getResult: () => {
					return { ...creditCardInfo };
				},
				isError: () => refIsRequiredError(fieldRefs.current),
				updateFields: newFields => {
					replaceCreditCardInfo(newFields);
				},
			}),
			// eslint-disable-next-line
			[creditCardInfo.cardName, creditCardInfo.cardExpiryDate, creditCardInfo.creditCardBankName, creditCardInfo.creditCardType, creditCardInfo.creditCardNo],
		);

		return (
			<Grid spacing={2} container>
				{showFields.map(field => renderFields(field))}
			</Grid>
		);
	},
);

export default CreditCardFields;
